import { type FormEvent, useEffect, useState } from "react"
import { toast } from "sonner"
import {
  type AppPermission,
  type AppUser,
  type CurrentUser,
  api,
} from "@/lib/api"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Separator } from "@/components/ui/separator"

type Props = {
  open: boolean
  editingUser: AppUser | null
  currentUser: CurrentUser
  onClose: () => void
  onSaved: (users: AppUser[]) => void
}

const ALL_PERMISSIONS: {
  value: AppPermission
  label: string
  description: string
}[] = [
  {
    value: "admin",
    label: "Admin",
    description:
      "Gerencia usuários do app, desativa acessos e altera permissões.",
  },
  {
    value: "create",
    label: "Criar",
    description:
      "Cria usuários VPN, edita e-mails e revoga ou reativa acessos.",
  },
  {
    value: "download",
    label: "Download",
    description: "Baixa arquivos .ovpn e reenvia acessos por e-mail.",
  },
  {
    value: "view",
    label: "Visualização",
    description: "Vê dashboard, logs, exportação e listagem da VPN.",
  },
]

export function AppUserFormSheet({
  open,
  editingUser,
  currentUser,
  onClose,
  onSaved,
}: Props) {
  const isEdit = editingUser !== null
  const [fullName, setFullName] = useState("")
  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [permissions, setPermissions] = useState<AppPermission[]>(["view"])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      if (editingUser) {
        setFullName(editingUser.fullName)
        setUsername(editingUser.username)
        setEmail(editingUser.email)
        setPassword("")
        setPasswordConfirm("")
        setPermissions([...editingUser.permissions])
      } else {
        setFullName("")
        setUsername("")
        setEmail("")
        setPassword("")
        setPasswordConfirm("")
        setPermissions(["view"])
      }
    }
  }, [open, editingUser])

  const togglePerm = (p: AppPermission) => {
    setPermissions((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    )
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (password && password !== passwordConfirm) {
      toast.error("A senha e a confirmação não coincidem.")
      return
    }
    if (permissions.length === 0) {
      toast.error("Selecione pelo menos uma permissão.")
      return
    }
    setLoading(true)
    try {
      let res: { users: AppUser[]; log: string }
      if (isEdit) {
        res = await api.updateAppUser(editingUser.username, {
          fullName,
          email,
          permissions,
          password: password || undefined,
        })
      } else {
        res = await api.createAppUser({
          username,
          fullName,
          email,
          permissions,
          password,
        })
      }
      onSaved(res.users)
      toast.success(res.log)
      onClose()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao salvar usuário."
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Editar usuário" : "Novo usuário"}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? `Editando ${editingUser?.username}. Deixe a senha em branco para mantê-la.`
              : "Preencha os dados para criar um novo acesso ao painel."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-5">
          <div className="space-y-2">
            <Label htmlFor="sf-fullname">Nome *</Label>
            <Input
              id="sf-fullname"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="sf-username">Usuário *</Label>
              <Input
                id="sf-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading || isEdit}
                autoComplete="off"
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sf-email">E-mail *</Label>
              <Input
                id="sf-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="off"
              />
            </div>
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">Permissões</p>
            <div className="flex flex-col gap-3">
              {ALL_PERMISSIONS.map((perm) => {
                const isOwn =
                  isEdit && editingUser?.username === currentUser.username
                const isLastAdmin =
                  perm.value === "admin" &&
                  isOwn &&
                  editingUser?.permissions.includes("admin")
                return (
                  <label
                    key={perm.value}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/40"
                  >
                    <Checkbox
                      checked={permissions.includes(perm.value)}
                      onCheckedChange={() => togglePerm(perm.value)}
                      disabled={loading || isLastAdmin}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <p className="text-sm leading-none font-medium">
                        {perm.label}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {perm.description}
                      </p>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">
              Segurança{isEdit ? " (opcional)" : ""}
            </p>
            <p className="mb-3 text-xs text-muted-foreground">
              Mín. 8 caracteres: maiúscula, minúscula, número e símbolo (! @ # $
              % * - _).
            </p>
            <div className="flex flex-col gap-3">
              <div className="space-y-2">
                <Label htmlFor="sf-pw">Senha {isEdit ? "" : "*"}</Label>
                <Input
                  id="sf-pw"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sf-pw-confirm">Confirmação</Label>
                <Input
                  id="sf-pw-confirm"
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading
                ? "Salvando..."
                : isEdit
                  ? "Salvar alterações"
                  : "Criar usuário"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
